import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import { omit } from 'lodash';
import 'animate.css/animate.css';
import { Store } from 'react-notifications-component';

import { GarmDefinition } from './GarmDefinition';
import { CloseIcon } from '@icons/CloseIcon';
import { ExportIcon } from '@icons/ExportIcon';
import { CaretIconUp } from '@icons/CaretIconUp';
import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';
import { RadioButton } from '@components/RadioButton';
import { SidebarItem } from '@insights_module/components/Sidebar';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { CategoryType, GarmFilter, GarmRiskLevelType } from '@insights_module/types/BrandSafety';
import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';
import { useEpisodeTraceability } from '@insights_module/hooks/useEpisodeTraceability';
import { useEpisodeWithDetails } from '@insights_module/hooks/useEpisodeWithDetails';
import { useEpisodeSegment } from '@insights_module/hooks/useEpisodeSegment';
import { download } from '@utils/download';
import { BookIcon } from '@icons/BookIcon';

const GarmCategoryItem = tw.div`w-full mb-1 rounded bg-page-background py-[6px] px-[8px]`;
const GarmCategoryHeader = tw.div`w-full grid grid-cols-[1fr] items-center gap-[8px]`;
const GarmCategoryContent = tw.div`overflow-hidden`;
const RiskItemContainer = tw.div`w-auto flex items-start justify-start text-[11px] font-medium text-text-and-icon rounded mt-[12px]
`;
const CategoryTitleButton = tw.button`w-full flex items-center gap-[8px] truncate cursor-pointer`;
const CategoryTitle = tw.span`text-[14px] text-text-and-icon font-semibold truncate`;
const RiskLevelIndicator = tw.div`w-[16px] h-[16px] min-w-[16px] min-h-[16px] rounded-[3px]`;
const Fallback = tw.div`bg-selected py-4 px-3 rounded-md text-text-and-icon-muted text-[13px] box-border mt-2 ml-[-4px] mr-1`;
const SmallTitle = tw.span`text-left text-[14px] font-semibold text-text-and-icon-muted tracking-[0.14px]`;
const RadioButtonWrapper = tw.div`flex items-center gap-[8px] bg-neutral-background p-[4px] pr-[8px] rounded-[5px] border border-solid border-sec-2 cursor-pointer text-[14px] font-medium`;
const RadioButtonLabel = tw.span`ml-1 whitespace-nowrap`;
const StyledButton = tw.button`border border-solid border-gray-1 rounded-[4px] p-[4px] bg-neutral-background flex items-center justify-center`;
const ControlsContainer = tw.div`flex items-center justify-end gap-[4px]`;
const Separator = tw.div`h-[15px] w-[1px] bg-gray-1`;
const selectedItemStyle = tw`shadow-small border border-solid border-gray-1 bg-gray-2`;
const FlexStart = tw.div`flex items-center justify-start gap-[6px]`;
const DownloadButton = tw(
  Button.Primary
)`w-full flex items-center justify-between py-[10px] px-[12px] h-auto [&_svg]:fill-current [&_svg]:w-4 [&_svg]:h-4 [&_svg]:transition-all [&_svg]:duration-200`;
const GarmDefinitionButton = tw.div`flex justify-center items-center cursor-pointer`;

export const riskLevelBackgroundStyle = {
  [GARM_RISK_LEVEL.unrelated]: tw`bg-garm-unrelated-bg`,
  [GARM_RISK_LEVEL.low]: tw`bg-garm-low-bg`,
  [GARM_RISK_LEVEL.medium]: tw`bg-garm-medium-bg`,
  [GARM_RISK_LEVEL.high]: tw`bg-garm-high-bg`,
  [GARM_RISK_LEVEL.floor]: tw`bg-floor-bg`,
};

export const EpisodeGarmSection = (): JSX.Element => {
  const { t } = useTranslation();
  const { episodeWithDetails, isLoading } = useEpisodeWithDetails();

  const hasGarmTracebility = episodeWithDetails.garmFilters?.some(
    (item) => item.risk !== GARM_RISK_LEVEL.unrelated
  );

  return (
    <>
      <SidebarItem hasDivider={true} isLoading={isLoading} header={null}>
        <SmallTitle>
          {t('insights.episode_details_drawer.episode_details_sidebar.garm_brand_safety.subtitle')}
        </SmallTitle>
        {!isLoading && !hasGarmTracebility ? (
          <Fallback>
            {t('insights.episode_details_drawer.episode_overview.garm_section.no_data')}
          </Fallback>
        ) : (
          <>
            <Spacer size='sm' direction='vertical' />
            {episodeWithDetails.garmFilters?.map((garm) => (
              <GarmItem key={garm.category} garm={garm} />
            ))}
          </>
        )}
      </SidebarItem>
    </>
  );
};

const GarmItem = ({ garm }: { garm: GarmFilter }): JSX.Element | null => {
  const { t } = useTranslation();
  const { episodeGarmSummary } = useEpisodeTraceability();
  const {
    selectedSegment,
    setSelectedSegment,
    selectedGarmCategory,
    setSelectedGarmCategory,
    selectedGarmLevel,
    setSelectedGarmLevel,
    setIsAudioPlaying,
  } = useEpisodeDetailsContext();
  const { segmentIdsFilteredByGarm } = useEpisodeSegment();

  const categorySummary = omit(episodeGarmSummary[garm.category], 'unrelated');
  const { isSingleRiskLevel, totalSegmentsInRisk } = useMemo(() => {
    return Object.values(categorySummary).reduce(
      (acc, val) => {
        if (val > 0) {
          acc.totalSegmentsInRisk += val;
          acc.riskLevelsInCategory++;
        }
        if (val > 0 && acc.riskLevelsInCategory > 1 && acc.isSingleRiskLevel) {
          acc.isSingleRiskLevel = false;
        }
        return acc;
      },
      { isSingleRiskLevel: true, totalSegmentsInRisk: 0, riskLevelsInCategory: 0 }
    );
  }, [categorySummary]);

  if (garm.risk === GARM_RISK_LEVEL.unrelated) return null;

  const handleGarmCategoryClick = () => {
    if (garm.risk === GARM_RISK_LEVEL.unrelated) return;
    if (selectedGarmCategory === garm.category) {
      setSelectedGarmCategory(null);
      setSelectedGarmLevel(null);
      return;
    }
    setIsAudioPlaying(false);
    setSelectedGarmCategory(garm.category);
  };

  const handleGarmLevelClick = (level: GarmRiskLevelType) => {
    if (selectedGarmLevel === level) {
      setSelectedGarmLevel(null);
      return;
    }
    setIsAudioPlaying(false);
    setSelectedGarmLevel(level);
  };

  const selectedIndex = useMemo(() => {
    if (!segmentIdsFilteredByGarm.length || selectedSegment === null) return -1;

    return segmentIdsFilteredByGarm.indexOf(selectedSegment);
  }, [segmentIdsFilteredByGarm, selectedSegment]);

  const handlePrev = () => {
    if (!selectedIndex) {
      setSelectedSegment(segmentIdsFilteredByGarm[segmentIdsFilteredByGarm.length - 1]);
      return;
    }

    setSelectedSegment(segmentIdsFilteredByGarm[selectedIndex - 1]);
  };

  const handleNext = () => {
    if (selectedIndex === segmentIdsFilteredByGarm.length - 1) {
      setSelectedSegment(segmentIdsFilteredByGarm[0]);
      return;
    }

    setSelectedSegment(segmentIdsFilteredByGarm[selectedIndex + 1]);
  };

  const handleDictionaryClick = (category: CategoryType, riskLevel: GarmRiskLevelType) => {
    Store.addNotification({
      insert: 'bottom',
      container: 'bottom-right',
      animationIn: ['animate__animated animate__fadeIn !rounded-md'],
      animationOut: ['animate__animated animate__fadeOut'],
      slidingEnter: {
        duration: 200,
        timingFunction: 'ease-out',
        delay: 0,
      },
      slidingExit: {
        duration: 200,
        timingFunction: 'ease-out',
        delay: 0,
      },
      dismiss: {
        duration: 0,
        showIcon: true,
      },
      width: 519,
      content: <GarmDefinition category={category} level={riskLevel} />,
    });
  };

  return (
    <GarmCategoryItem
      css={[
        garm.risk === GARM_RISK_LEVEL.unrelated && tw`cursor-auto`,
        selectedGarmCategory === garm.category && selectedItemStyle,
      ]}>
      <GarmCategoryHeader
        css={[selectedGarmCategory === garm.category && tw`grid-cols-[1fr_80px]`]}>
        <CategoryTitleButton onClick={() => handleGarmCategoryClick()}>
          <RiskLevelIndicator css={[riskLevelBackgroundStyle[garm.risk]]} />
          <CategoryTitle>{t(`insights.garm_category.${garm.category}`)}</CategoryTitle>
        </CategoryTitleButton>

        {selectedGarmCategory === garm.category && (
          <ControlsContainer>
            <StyledButton onClick={handlePrev}>
              <CaretIconUp tw='stroke-text-and-icon-muted w-[12px] h-[12px]' />
            </StyledButton>
            <StyledButton onClick={handleNext}>
              <CaretIconUp tw='stroke-text-and-icon-muted rotate-[180deg] w-[12px] h-[12px]' />
            </StyledButton>
            <Separator />
            <StyledButton onClick={() => handleGarmCategoryClick()}>
              <CloseIcon tw='fill-text-and-icon-muted w-[12px] h-[12px]' />
            </StyledButton>
          </ControlsContainer>
        )}
      </GarmCategoryHeader>

      {selectedGarmCategory === garm.category && !isSingleRiskLevel && (
        <RiskItemContainer css={[!selectedGarmLevel && tw`text-accent-1 bg-gray-2`]}>
          <RadioButtonWrapper>
            <RadioButton
              selected={!selectedGarmLevel}
              onChange={() => {
                setSelectedGarmLevel(null);
              }}>
              <RadioButtonLabel>{`Select All (${totalSegmentsInRisk})`}</RadioButtonLabel>
            </RadioButton>
          </RadioButtonWrapper>
        </RiskItemContainer>
      )}

      {categorySummary ? (
        <GarmCategoryContent
          css={[selectedGarmCategory === garm.category ? tw`h-full min-h-[10px]` : tw`h-0`]}>
          {Object.entries(categorySummary).map(([key, val]) => {
            if (key === GARM_RISK_LEVEL.unrelated || val === 0) return null;

            const isSelected = key === selectedGarmLevel;

            // if we have value for only one risk level in selected category, select it by default
            if (selectedGarmCategory === garm.category && isSingleRiskLevel) {
              setSelectedGarmLevel(key as GarmRiskLevelType);
            }

            return (
              <RiskItemContainer key={key} css={[isSelected && tw`text-accent-1 bg-gray-2`]}>
                <RadioButtonWrapper css='w-full'>
                  <RadioButton
                    selected={isSelected}
                    onChange={() => {
                      if ((val as number) < 1 || isSingleRiskLevel) return;
                      handleGarmLevelClick(key as GarmRiskLevelType);
                    }}>
                    <RadioButtonLabel>
                      {`${t(`insights.garm_risk_level.${key}`)} (${val})`}
                    </RadioButtonLabel>
                  </RadioButton>
                  <GarmDefinitionButton
                    onClick={() => handleDictionaryClick(garm.category, key as GarmRiskLevelType)}>
                    <BookIcon tw='w-[16px] h-[16px] stroke-text-and-icon-muted' />
                  </GarmDefinitionButton>
                </RadioButtonWrapper>
              </RiskItemContainer>
            );
          })}
        </GarmCategoryContent>
      ) : null}
    </GarmCategoryItem>
  );
};

export const DownloadGarmButton = () => {
  const { t } = useTranslation();
  const { selectedEpisode } = useInsightsContext();
  const { episodeGarmSummary } = useEpisodeTraceability();

  const handleDownloadClick = useCallback(() => {
    const fileName = `garm-${selectedEpisode}.txt`;

    const textData = Object.entries(episodeGarmSummary)
      .map(([key, val]) => {
        const cat = t(`insights.garm_category.${key}`);
        const vals = Object.entries(val)
          .map(([key, value]) => `  ${t(`insights.garm_risk_level.${key}`)} - ${value}`)
          .join('\n');

        return `${cat}\n${vals}`;
      })
      .join('\n\n');

    const file = new Blob([textData], {
      type: 'text/plain',
    });

    download(file, fileName);
  }, [episodeGarmSummary]);

  return (
    <DownloadButton onClick={handleDownloadClick}>
      <FlexStart>
        <ExportIcon />
        {t(
          'insights.episode_details_drawer.episode_details_sidebar.garm_brand_safety.download_button'
        )}
      </FlexStart>
    </DownloadButton>
  );
};
